import { Box, Button } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import styles from '../ConsultDoctor.module.css'
import * as api from '../../../services/ApiConsultDoctor'
import LoaderAnimation from '../../../components/LoaderAnimation'
import tabBloodPrNormal from '../../../assets/img/tab_blood_pressure_narmal.svg'

import { notifyError, notifyInfo } from "../../../components/NotifyToast";

import { Bar } from 'react-chartjs-2';

import { MdOutlineSummarize } from "react-icons/md";
import { FaFireAlt } from "react-icons/fa";
import { GiWeight } from "react-icons/gi";
import { GiFat } from "react-icons/gi";
import { FaRegHeart } from "react-icons/fa";
import { GiNightSleep } from "react-icons/gi";
import { PiSmileySad } from "react-icons/pi";
import { SiOxygen } from "react-icons/si";

import { config } from '../../../config.env'

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    TimeScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
    CategoryScale,
    LinearScale,
    TimeScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

const domain = config.API_ENDPOINT;


function bmrCalculate(isMale,kg,cm,years) {
    if (isMale)
        return 88.362 + (13.397*kg) + (4.799*cm) - (5.677*years);
    else return 447.593 + (9.247*kg) + (3.098*cm) - (4.33*years);
}

const chartInfo = {
    "summary": {
        title:"DAILY SUMMARY",
        icon:<MdOutlineSummarize size={16}/>,
        apiField: "KCAL_BURNED",
        graphTitle: "Daily Calories Burned (kcal)",
        graphKey:"",
        graphMax:3000,
        graphIcon:<FaFireAlt size={16}/>,
        grader: (burned, bmr=1800) => {
            let kcalGrade;
            if (burned < bmr)
                kcalGrade = (1-((bmr-burned)/1000))/1.5;
            else kcalGrade = Math.min(1-((burned-bmr-500)/1000),1);
            return Math.max(0,kcalGrade);
        }
    },
    "body":{
        title:"WEIGHT",
        icon:<GiWeight size={16}/>,
        apiField: "BODY_FAT",
        graphTitle: "Daily Body Fat (%)",
        graphKey:"",
        graphMax:50,
        graphIcon:<GiFat size={16}/>,
        grader: (fat, gender = "male") => {
            const fatBest = (gender === "male") ? 16 : 28.5;
            return Math.max(0,Math.min(1-(Math.abs(fatBest-fat)/18),1));
        }
    },
    "heart":{
        title:"HEART RATE",
        icon:<FaRegHeart size={12}/>,
        apiField: "RESTING_HEART_RATE",
        graphTitle: "Daily Resting Pulse Rate (BPM)",
        graphKey:"restBpm",
        graphMax:135,
        graphIcon:<FaRegHeart size={12}/>,
        grader: (value) => { return Math.max(0,Math.min(1-((value-60)/40),1)); }
    },
    "pressure":{
        title:"BLOOD PRESSURE",
        icon:<FaRegHeart size={12}/>,
        apiField: "BLOODPRESSURE",
        graphTitle: "Daily Blood Pressure Level (mmHg)",
        graphKey:"BLOOD_PRESSURE_SYSTOLIC",
        graphKeyB:"BLOOD_PRESSURE_DIASTOLIC",
        graphMax:210,
        graphIcon:<FaRegHeart size={12}/>,
        grader: (value) => { return Math.max(0,Math.min(1-Math.abs(value-105)/35,1)); },
        graderB: (value) => { return Math.max(0,Math.min(1-Math.abs(value-70)/20,1)); }
    },
    "sleep":{
        title:"SLEEP",
        icon:<GiNightSleep size={16}/>,
        apiField: "SLEEP_SCORE",
        graphTitle: "Daily Sleep Score",
        graphKey:"",
        graphMax:100,
        graphIcon:<GiNightSleep size={16}/>,
        grader: (value) => { return Math.max(0,(value-50)/50); }
    },
    "stress":{
        title:"STRESS",
        icon:<PiSmileySad size={16}/>,
        apiField: "STRESS_SCORE",
        graphTitle: "Daily Average Stress Score",
        graphKey:"",
        graphMax:100,
        graphIcon:<PiSmileySad size={16}/>,
        grader: (value) => { return Math.max(0,Math.min(1-((value-15)/65),1)); }
    },
    "spo2":{
        title:"OXYGEN LEVEL",
        icon:<SiOxygen size={12}/>,
        apiField: "BLOOD_OXYGEN_SATURATION",
        graphTitle: "Daily Average SpO2 (%)",
        graphKey:"spo2",
        graphMax:100,
        graphIcon:<SiOxygen size={12}/>,
        grader: (value) => { return Math.min(Math.max(0,(value-80)/20),1); },
    }
};

function CardPage({ cardsInfo, onClickCard })
{
    const ChartItem = ({ type }) =>
    {
        const info = cardsInfo === null ? null : cardsInfo[type];
        if (info === null)
            return <></>;

        const title = chartInfo[type].title;
        const grade = info.grade;
        const clickable = (chartInfo[type].apiField !== '');

        let scale = 1;
        let barColor = '#E1E3E9';
        if (grade <= 1)
        {
            if (grade <= 0.4)
                scale = ((1-grade)*0.05) + 1;
            barColor = "hsl("+(grade*120).toString()+",80%,50%)";
        }

        return (
            <Box onClick={() => { if (clickable) onClickCard(type); }} sx={{
                cursor: (clickable) ? 'pointer' : 'auto',
                display: 'grid',
                alignItems: 'start',
                mx: 'auto',
                padding: 2,
                minHeight: 300 * scale,
                minWidth: 250,
                border: (grade <= 0.5 ? scale*2:1),
                borderRadius: '10px',
                borderColor: (grade <= 0.5 ? barColor:'#E1E3E9'),
                bgcolor: '#FFF',
                borderBottom:(grade <= 1 ? 8:1),
                borderBottomColor:barColor,
                fontSize: 18,
                '@media (max-width: 500px)': {
                    fontSize: 16
                }
            }}
            >
                <Box sx={{
                    marginBottom:1,
                    color: '#082C91',
                    fontWeight: 700
                }}>
                    {title}&nbsp; {chartInfo[type].icon}
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    {(() => {
                        if (type === "summary")
                        {
                            return (
                            <Box sx={{ fontSize: 16, '@media (max-width: 500px)': {fontSize: 14} }}>
                                <div><pre>Calories Burned  <b>{info.kcal} kcal</b><br/><br/></pre></div>
                                <div><pre>Active Time  <b>{Math.floor(info.active/60)} h {info.active % 60} min</b><br/><br/></pre></div>
                                <div><pre>Steps  <b>{info.steps}</b><br/><br/></pre></div>
                                <div><pre>Distance  <b>{info.dist} m</b></pre></div>
                            </Box>);
                        }
                        else if (type === "body")
                        {
                            return (<>
                                <div><pre>Weight  <b>{info.weight} kg</b><br/><br/></pre></div>
                                <div><pre>Muscle Mass  <b>{info.muscle} kg</b><br/><br/></pre></div>
                                <div><pre>Body Fat  <b>{info.fat} %</b></pre></div>
                            </>);
                        }
                        else if (type === "heart")
                        {
                            return (<>
                                <div>Daily Average Pulse<br/> <b>{info.avg} BPM</b><br/><br/></div>
                                <div>Resting Rate<br/> <b>{info.rest} BPM</b></div>
                            </>);
                        }
                        else if (type === "pressure")
                        {
                            return (<>
                                <div>Systolic Level<br/> <b>{info.sys} mm Hg</b><br/><br/></div>
                                <div>Diastolic Level<br/> <b>{info.dia} mm Hg</b></div>
                            </>);
                        }
                        else if (type === "sleep")
                        {
                            return (<>
                                <div>Daily Sleep Score<br/> <b>{info.score} / 100</b><br/><br/></div>
                                <div>Total Time<br/> <b>{Math.floor(info.total/60)} h {info.total % 60} min</b></div>
                            </>);
                        }
                        else if (type === "stress")
                        {
                            return (<>
                                <div>Daily Stress Score<br/> <b>{info.avg} / 100</b></div>
                            </>);
                        }
                        else if (type === "spo2")
                        {
                            return (<>
                                <div>Daily Average SpO2<br/> <b>{info.avg} %</b></div>
                            </>);
                        }
                    })()}
                </Box>

                <Box sx={{
                    justifySelf:'end',
                    alignSelf:'end',
                    fontSize:'small',
                    color:'gray',
                }}>
                    {new Date(info.date).toDateString()}
                </Box>

            </Box>
        );
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'start',
            alignItems: 'center',
            overflow: "hidden",
            mt:2, mx: 2,
            fontSize: 18,
            '@media (max-width: 500px)': { fontSize: 14 },
            '@media (max-width: 600px)': { width: 350 }
        }}>
            <Box sx={{
                border: 1,
                borderRadius: '10px',
                borderColor: '#E1E3E9',
                bgcolor: '#FFF',
                alignSelf: 'stretch'
            }}>
                <Box sx={{
                    color: '#082C91',
                    fontWeight: 700,
                    backgroundColor: '#E3E9F2',
                    borderBottom: '1px solid #E1E3E9',
                    borderRadius: '10px',
                    padding: '10px 20px'
                }}>Data Information</Box>

                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr',
                    gridGap: '20px',
                    padding: '20px',
                    '@media (max-width: 1023px)': {
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gridGap: '15px',
                        padding: '15px',
                    },
                    '@media (max-width: 800px)': {
                        gridTemplateColumns: '1fr',
                        gridGap: '10px',
                        padding: '10px',
                    }
                }}>
                    <div>
                        <div style={{ fontWeight: 700, color: '#082C91' }}>Device Model</div>
                        <div>{cardsInfo === null ? "-" : cardsInfo.device.device}</div>
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, color: '#082C91' }}>Device Firmware</div>
                        <div>{cardsInfo === null || cardsInfo.device.firmware === undefined ? "-" : cardsInfo.device.firmware}</div>
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, color: '#082C91' }}>Date</div>
                        <div>{cardsInfo === null || cardsInfo.date === undefined ? "-" : cardsInfo.date}</div>
                    </div>
                </Box>
            </Box>

            { cardsInfo === null ? <div style={{ textAlign: 'center', margin: '20%', fontSize: 28 }}>ไม่พบข้อมูล</div> :
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignContent: 'space-between',
                    gridGap: 20,
                    padding: 2,
                    marginLeft: 5,
                    marginRight: 5,
                }}>
                    <ChartItem type="summary"/>
                    <ChartItem type="body"/>
                    <ChartItem type="heart"/>
                    <ChartItem type="pressure"/>
                    <ChartItem type="sleep"/>
                    <ChartItem type="stress"/>
                    <ChartItem type="spo2"/>
                </Box>
            }
        </Box>
    );
}

function GraphPage({ graphPage, chartRef, barOptions, barData, dateRange, limitRange, onClickBack, onClickRange })
{
    return (
        <Box sx={{
            display:'block',
            fontSize:18,
            mt:5,
            mx:2,
            color:'#082C91',
            '@media (max-width: 500px)': { mt:1 },
            '@media (max-width: 600px)': { width: 350 }
        }}>

            { limitRange >= 1 &&
                <Button onClick={onClickBack} variant='outlined' sx={{ cursor: 'pointer', display:'inline'}}>
                    &lt; &nbsp; ย้อนกลับ
                </Button>
            }

            <Box sx={{ display:'flex', flexWrap:'wrap', justifyContent:(window.innerWidth >= 512 ? 'space-between':'center'), mt:3, mb:1, mx:3, gap:1 }}>
                <Box sx={{display: 'inline', alignContent:'flex-end', '@media (max-width: 512px)': { fontSize: 'smaller' } }}>
                    {chartInfo[graphPage].graphIcon}&nbsp; <b>{chartInfo[graphPage].graphTitle}</b>
                </Box>
                { limitRange >= 1 ?
                    <Box sx={{display: 'inline-grid', width:{lg:'40%'}, gridTemplateColumns:'1fr '.repeat(1+limitRange), gap:1 }}>

                        <Button
                            onClick={() => { onClickRange(0); }}
                            variant={dateRange === 0 ? 'contained' : 'outlined'}
                            sx={{ '@media (max-width: 512px)': { padding: 1 } }}
                        >
                            1 Week
                        </Button>

                        <Button
                            onClick={() => { onClickRange(1); }}
                            variant={dateRange === 1 ? 'contained' : 'outlined' }
                            sx={{ '@media (max-width: 1024px)': { padding: 1 } }}
                        >
                            2 Weeks
                        </Button>

                         {limitRange >= 2 && <Button onClick={() => { onClickRange(2); }} variant={dateRange === 2 ? 'contained' : 'outlined'}>1 Month</Button> }
                        { limitRange >= 3 && <Button onClick={() => { onClickRange(3); }} variant={dateRange === 3 ? 'contained' : 'outlined'}>3 Months</Button> }

                    </Box>
                    : <Box sx={{fontSize:'small', alignContent:'center'}}><i>[ 1 Week ]</i></Box>

                }
            </Box>

            <Box sx={{p:{xs:1,md:2,lg:3,xl:4}, mt:2, borderRadius: '13px', bgcolor: '#E3E9F2', }}>
                <Box sx={{ display: 'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', gap:2, py:2, borderRadius: '13px', bgcolor: '#FFFFFF', }}>
                    <Box sx={{
                        display: 'inline',
                        width:{xs:'300px',sm:'320px',md:'480px',lg:'640px',xl:'1024px'},
                        height:{xs:'180px',sm:'200px',md:'240px',lg:'320px',xl:'512px'},
                    }}>
                        <Bar
                            ref={chartRef}
                            options={barOptions}
                            data={barData}
                            style={{
                                alignSelf:'center'
                            }}
                        />
                    </Box>
                    { limitRange >= 1 &&
                        <Box sx={{ display: 'inline' }}>
                            <img className={styles.Img}
                                src={tabBloodPrNormal}
                                alt='color legend'
                            />
                        </Box>
                    }
                </Box>
            </Box>

            { limitRange < 1 &&
                <Button onClick={onClickBack} variant='outlined' sx={{ cursor: 'pointer', display:'block', mt:3, justifySelf:'flex-end'}}>
                    &lt; &nbsp; ย้อนกลับ
                </Button>
            }

        </Box>
    );
}

export default function SmartWatch({ PID, hwRegistered })
{
    const [loading, setLoading] = useState(false);

    const [cardsInfo, setCardsInfo] = useState(null);
    const [graphPage, setGraphPage] = useState('');

    //hold data for each API fields; which holds: labels[], data[], colors[], (and dataB[], colors[] for BP)
    const fetchedData = useRef(null);
    const sampledLength = useRef(0);

    const chartRef = useRef();
    const [barOptions, setBarOptions] = useState(null);
    const [barData, setBarData] = useState(null);

    const [limitRange, setLimitRange] = useState(3);
    const prefDateRange = useRef(0);

    // Reading state's value (React Hook useState) with event listeners may have inconsistent and unpredictability
    // There are various workarounds, but I do so by:
    // Using dateRange state normally, but read from dateRangeRef.current instead when in event callbacks, and call a 'psuedo setState' function when setting value
    const dateRangeRef = useRef(0);
    const [dateRange, setDateRangeState] = useState(0);
    const setDateRange = (value) => {
        dateRangeRef.current = value;
        setDateRangeState(value);
    };

    const onClickBack = () => {
        setGraphPage('');
        setBarOptions(null);
        setBarData(null);
    };

    const onClickRange = (range) => {
        prefDateRange.current = range;
        setDateRange(range);
    };

    const chartReset = () => {
        setBarOptions({
            plugins: {
                legend: { display: false },
                tooltip: { enabled: false },
                datalabels: {}
            },
            scales: {
                y: {
                    beginAtZero: true,
                    border: { display: false },
                    ticks: { font: { size: '12%' } },
                },
                x: { ticks: { display:false } }
            }
        });

        setBarData({
            labels:[],
            datasets:[]
        });
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => { window.removeEventListener("resize", handleResize); };
    }, []);

    useEffect(() => {
        if (hwRegistered)
            getSmartWatch();
    }, [hwRegistered, PID]);

    useEffect(() => {
        if (graphPage === '')
            return;
        else if (cardsInfo == null)
        {
            chartReset();
            setGraphPage('');
            return;
        }

        const field = chartInfo[graphPage].apiField;
        getSmartWatchGraph(field);

    }, [cardsInfo, graphPage, dateRange]);

    const getSmartWatch = async () =>
    {
        setLoading(true);
        let res = await api.getSmartWatch(PID);
        setLoading(false);

        fetchedData.current = {};
        setCardsInfo(null);
        
        if (!res)
            notifyError('Fetch Error');
        else if (res['data']['code'] !== 200)
            notifyInfo(res['data']['message']);
        else
        {
            const data = res['data']['data']['huawei_health'];

            if (data !== null && data.device !== undefined && data.device.type !== undefined && data.user !== undefined)
            {
                const gender = data.user.gender;
                const age = data.user.age;
                const weight = data.user.weight;
                const height = data.user.height;

                const bmr = bmrCalculate((gender === "male"),weight,height,age);
                const kcal = data.daily_summary.calories_burned;

                const fat = data.body_metrics[0].body_fat_percentage;
                const pulseRest = data.heart_rate.resting_heart_rate;

                const sys = Math.round((0.75+Math.random()/2)*105);
                const dia = Math.round((0.75+Math.random()/2)*70);
                const gradeSys = chartInfo.pressure.grader(sys);
                const gradeDia = chartInfo.pressure.graderB(dia);

                const cardsData = {
                    date: data.date,
                    device: {
                        device: data.device.type,
                        firmware: data.device.firmware_version, },
                    user: {
                        age: age,
                        gender: gender,
                        weight: weight,
                        height: height, },
                    summary: (data.daily_summary === undefined ? null : {
                        date: Date.parse(data.date),
                        kcal: kcal,
                        active: data.daily_summary.active_time/60000,
                        steps: data.daily_summary.steps,
                        dist: data.daily_summary.distance,
                        grade: chartInfo.summary.grader(kcal,bmr), }),
                    body: (data.body_metrics === undefined ? null : {
                        date: Date.parse(data.body_metrics[0].measurement_time),
                        bmi: data.body_metrics[0].bmi,
                        weight: data.body_metrics[0].weight,
                        muscle: data.body_metrics[0].muscle_mass,
                        fat: fat,
                        grade: chartInfo.body.grader(fat,gender), }),
                    heart: (data.heart_rate === undefined ? null : {
                        date: Date.parse(data.heart_rate.result[0].timestamp),
                        avg: data.heart_rate.daily_average,
                        rest: pulseRest,
                        grade: chartInfo.heart.grader(pulseRest), }),
                    pressure: {
                        date: Date.parse(data.heart_rate.result[0].timestamp),
                        sys: sys,
                        dia: dia,
                        grade: Math.min(gradeSys,gradeDia),
                        gradeA: gradeSys,
                        gradeB: gradeDia, },
                    sleep: (data.sleep === undefined ? null : {
                        date: Date.parse(data.sleep.end_time),
                        score: data.sleep.sleep_score,
                        total: data.sleep.total_duration/60000,
                        grade: chartInfo.sleep.grader(data.sleep.sleep_score), }),
                    stress: (data.stress === undefined ? null : {
                        date: Date.parse(data.stress.result[0].timestamp),
                        avg: data.stress.daily_average,
                        grade: chartInfo.stress.grader(data.stress.daily_average), }),
                    spo2: (data.spo2 === undefined ? null : {
                        date: Date.parse(data.spo2.result[0].timestamp),
                        avg: data.spo2.daily_average,
                        grade: chartInfo.spo2.grader(data.spo2.daily_average), }),
                };
                
                setCardsInfo(cardsData);
            }
        }
    }

    const handleResize = () => {

        const w = window.innerWidth;
        
        const limit = Math.min(Math.floor(w/512),3);
        setLimitRange(limit);

        let largeSpan = false;
        if (dateRangeRef.current > limit)
        {
            setDateRange(limit);
            largeSpan = (limit > 2);
        }
        else if (prefDateRange.current <= limit && dateRangeRef.current !== prefDateRange.current)
        {
            setDateRange(prefDateRange.current);
            largeSpan = (prefDateRange.current > 2);
        }

        if (barOptions != null && barData != null)
        {
            const xOptions = barOptions.scales.x;
            const gplugins = barOptions.plugins;
            const ticksDisplay = (barData.labels.length <= 7 || w >= 512);
        
            if (xOptions.ticks.display !== ticksDisplay)
            {
                const sampledLength = fetchedData.current.values[0].toString().length;

                gplugins.datalabels.formatter = (value) => {
                    if (largeSpan || w<512) {
                        if (value >= 1000)
                            value = (value/1000).toFixed(1) + 'k';
                        else if (sampledLength >= 4)
                            value = Math.round(value);
                    }
                    return value;
                }

                xOptions.ticks.display = ticksDisplay;

                setBarOptions({
                    plugins: gplugins,
                    scales: {
                        y: barOptions.scales.y,
                        x: xOptions
                    }
                });
            }
        }
    };

    const getSmartWatchGraph = async (field) =>
    {
        const populate = false;
        let doMockUp = false;
        let range = dateRange;
        if (populate)
        {
            doMockUp = true;
            range = 3;
        }

        const timeNow = Math.floor(Date.now() / 86400000) * 86400000;   // get ms of the start of today
        const rangeMs = 86400000 * (range <= 1 ? (range === 0 ? 6 : 13) : (range === 2 ? 30 : 92));
        let date_to = timeNow;
        let date_from = timeNow - rangeMs;

        setLoading(true);
        let res = await api.getSmartWatchRecords(PID, field, new Date(date_from).toISOString().slice(0,10), new Date(date_to).toISOString().slice(0,10));
        setLoading(false);
        
        fetchedData.current = null;
        let fetched;
        let dataCount = 0;

        if (!res)
            notifyError('Fetch Error');
        else if (res['data']['code'] !== 200)
            notifyInfo(res['data']['message']);
        else
        {
            fetchedData.current = {
                labels: [],
                values: [],
                colors: [],
            };
            fetched = fetchedData.current;

            if (field === "BLOODPRESSURE")
            {
                fetched.valuesB = [];
                fetched.colorsB = [];
            }

            const recordList = res['data']['data']['huawei_health_record_list'];
            dataCount = recordList.length;
            //console.log(recordList);

            // Mock Up Populate
            if (doMockUp)
            {
                const key = chartInfo[graphPage].graphKey;
                const keyB = field === "BLOODPRESSURE" ? chartInfo[graphPage].graphKeyB : false;

                if (dataCount <= 0)
                {
                    recordList.push({
                        'date': new Date(date_from).toISOString().slice(0,10),
                        [key]: Math.round((chartInfo[graphPage].graphMax/2) * (0.5 + Math.random())),
                    })
                    dataCount++;
                }

                const today = Date.now();
                date_to = today;
                let currDate = new Date(recordList[dataCount-1].date).getTime() + Math.round(Math.random() * 7 * 86400000);
                const firstValue = recordList[dataCount-1][key];
                while (currDate < today)
                {
                    const data = {
                        'date': new Date(currDate).toISOString().slice(0,10),
                        [key]: Math.round(firstValue * (0.8 + (Math.random()*0.3))),
                    };

                    if (keyB)
                        data[keyB] = Math.round((data[key]-70+(Math.random()*40)));

                    recordList.push(data);
                    dataCount++;

                    currDate += Math.round(Math.random() * 7 * 86400000);
                }
            }
            //

            const key = chartInfo[graphPage].graphKey;
            const grader = chartInfo[graphPage].grader;
            const keyB = field === "BLOODPRESSURE" ? chartInfo[graphPage].graphKeyB : false;
            const graderB = keyB ? chartInfo[graphPage].graderB : undefined;
            recordList.slice().reverse().forEach((record,i) => {

                const date = record.date;

                const value = record[key];
                fetched.labels.unshift(date);
                fetched.values.unshift(value);
                fetched.colors.unshift("hsl("+Math.round(grader(value)*120).toString()+",80%,50%,0.75)");
                if (keyB)
                {
                    const valueB = record[keyB];
                    fetched.valuesB.unshift(valueB);
                    fetched.colorsB.unshift("hsl("+Math.round(graderB(valueB,true)*120).toString()+",80%,40%,1)");
                }
            });
        }

        // Prevent x-shifting animations when data updates
        if (chartRef.current != null)
        {
            chartRef.current.data = [];
            chartRef.current.update('none');
        }

        if (dataCount <= 0)
            chartReset();
        else
        {
            const sampledLength = fetched.values[0].toString().length;
            const xSize = 16 - (dataCount/15);
            const labelSize = xSize - Math.round(sampledLength/3);
            const doFormat = (dataCount > 30 || limitRange < 1);

            setBarOptions({
                plugins: {
                    legend: { display: false },
                    tooltip: { enabled: false },
                    datalabels: {
                        anchor: 'end',
                        align: function(context) { return context.datasetIndex === 0 ? 'end' : 'start'; },
                        offset: function(context) { return context.datasetIndex === 0 ? -5 : 5; },
                        textStrokeWidth: function(context) { return context.datasetIndex === 0 ? 0 : 4; },
                        textStrokeColor: '#FFF',
                        font: { size: labelSize.toString()+'%' },
                        formatter: function(value) {
                            if (doFormat) {
                                if (value >= 1000)
                                    value = (value/1000).toFixed(1) + 'k';
                                else if (sampledLength >= 4)
                                    value = Math.round(value);
                            }
                            return value;
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        suggestedMax: chartInfo[graphPage].graphMax,
                        border: { display: false },
                        ticks: { font: { size: '16%' } },
                    },
                    x: {
                        border: { display: false },
                        grid: { display: false },
                        ticks: { display:(dataCount <= 7 || limitRange >= 1), font: {size: xSize+'%' } }
                    }
                }
            });

            const thisBarData = {
                labels: fetched.labels,
                datasets: [{
                    label: '',
                    data: fetched.values,
                    backgroundColor: fetched.colors,
                    grouped: false,
                }]
            };

            if (graphPage === "pressure")
            {
                thisBarData.datasets.push({
                    label:"dia",
                    data:fetched.valuesB,
                    backgroundColor:fetched.colorsB,
                });
            }
            
            setBarData({
                labels:thisBarData.labels,
                datasets:[...thisBarData.datasets]
            });
        }
    }

    return ( <>
        <LoaderAnimation loading={loading} />

        <Box sx={{
            textAlign: 'start',
            ml:2,
            color: '#082C91',
            fontSize: '24px',
            '@media (max-width: 512px)': { textAlign: 'center', }
        }}>
            ข้อมูลจาก Smart Watch
        </Box>

        { hwRegistered ? 
            ( (graphPage === '' || barOptions === null || barData === null) ?
                <CardPage cardsInfo={cardsInfo} onClickCard={setGraphPage} />
                :
                <GraphPage {...{graphPage, chartRef, barOptions, barData, dateRange, limitRange, onClickBack, onClickRange}} />
            )
            :
            <div style={{ textAlign: 'center', margin: '20%', fontSize: 28 }}>
                <Box sx={{marginBottom: 2}}><b>Huawei ID Unregistered</b></Box>
                <Button
                    sx={{fontSize:18}}
                    onClick={() => {
                        const newWindow = window.open(`${domain}/huawei/auth/huawei?pid=${PID}`, '_blank', 'noopener,noreferrer');
                        if (newWindow) newWindow.opener = null;
                    }}
                    variant={'outlined'}
                >
                    Register Now
                </Button>
            </div>
        }
    </>);
}